import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { typography } from 'src/config/typography';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { colors } from 'src/config/colors';
import { useMediaQuery } from 'src/common/migration/useMediaQuery';
import { AboutUsPossibilities } from 'src/common/models/aboutUsStaticPage';
import { splitEvery } from 'ramda';
import Blocks from 'editorjs-blocks-react-renderer';
import { editorjsRenderers } from 'src/config/editorjs';
import { StaticPageEditorContent } from 'src/public/components/static-page/StaticPageEditorContent';

const Box = styled.div`
  display: flex;
  justify-content: center;
  max-width: 26.875rem;
  padding: 0 0.75rem 0;
  width: 100%;

  ${media.w.min.px768(css`
    display: block;
    width: 50%;
  `)};
`;

const Container = styled.div`
  align-items: center;
  background: ${colors.blue};
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  padding: 3.75rem 0.75rem;
  width: 100%;

  ${media.w.min.px768(css`
    align-items: flex-start;
    flex-direction: row;
    padding: 3.75rem 2.625rem;
  `)};
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 21.875rem;
  padding: 0 0.75rem 0;
  text-align: center;

  ${media.w.min.px768(css`
    align-items: flex-start;
    padding: 0 2rem 0 0;
    text-align: start;
    width: 50%;
  `)};
`;

const Group = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin-top: 1.875rem;
  padding-left: 0;

  ${media.w.min.px768(css`
    flex-direction: row;
    flex-wrap: wrap;
  `)}
`;

const Heading = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1.5rem;
  line-height: 1.875rem;
  margin-top: 1rem;
`;

const Item = styled.li`
  font-family: ${typography.fontFamilyDefault};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  line-height: 1rem;
  padding-left: 1rem;
  position: relative;
  text-transform: uppercase;

  ${media.w.max.px768(css`
    font-size: 0.625rem;
    letter-spacing: 0.15em;
    line-height: 0.875rem;
    max-width: 10.375rem;
  `)}

  &:not(:last-of-type) {
    margin-bottom: 1.875rem;
  }

  &::before {
    content: '•';
    font-size: 1rem;
    left: 0;
    position: absolute;
    text-align: center;
  }

  a:link,
  a:visited {
    color: ${colors.white};
    text-decoration: underline;
  }

  a:hover {
    cursor: pointer;
  }

  p {
    margin: 0 0 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Subtitle = styled.div`
  color: ${colors.pfp3};
  font-family: ${typography.fontFamilyDefault};
  font-size: 0.75rem;
  letter-spacing: 0.15rem;
  line-height: 1rem;
  text-transform: uppercase;
`;

const Text = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  line-height: 1.375rem;
  margin-top: 1.875rem;

  p {
    margin: 0 0 1.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export interface AboutUsPossibilitiesSectionProps {
  content: AboutUsPossibilities;
}

export const AboutUsPossibilitiesSection: FC<AboutUsPossibilitiesSectionProps> = ({ content }) => {
  const isMobile = useMediaQuery({ maxWidth: below(widthBreakpoints.px768) });

  const mobileItems = useMemo(() => splitEvery(4, content.list), [content]);

  return (
    <Container>
      <Content>
        <Subtitle>{content.subtitle}</Subtitle>
        <Heading>{content.heading}</Heading>

        <Text>
          <StaticPageEditorContent>
            <Blocks data={content.text} renderers={editorjsRenderers} />
          </StaticPageEditorContent>
        </Text>
      </Content>

      <Box>
        {isMobile ? (
          mobileItems.map((group, index) => (
            <Group key={index}>
              {group.map((item) => (
                <Item key={item.id}>
                  <StaticPageEditorContent>
                    <Blocks data={item.text} renderers={editorjsRenderers} />
                  </StaticPageEditorContent>
                </Item>
              ))}
            </Group>
          ))
        ) : (
          <Group>
            {content.list.map((item) => (
              <Item key={item.id}>
                <StaticPageEditorContent>
                  <Blocks data={item.text} renderers={editorjsRenderers} />
                </StaticPageEditorContent>
              </Item>
            ))}
          </Group>
        )}
      </Box>
    </Container>
  );
};
